/**
 * GSAP Animations for Germinate Theme
 * Subtle scroll-triggered animations for blocks
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Reduced Motion Support
 * Disable animations for users who prefer reduced motion
 */
function initReducedMotionSupport() {
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

	if (prefersReducedMotion.matches) {
		// Kill all ScrollTriggers
		ScrollTrigger.getAll().forEach(st => st.kill());

		// Show all elements immediately without animation
		document.querySelectorAll('[style*="opacity: 0"]').forEach(el => {
			el.style.opacity = '1';
			el.style.transform = 'none';
		});

		// Also handle elements that might be set via CSS classes
		const animatedElements = document.querySelectorAll('.gsap-animate, [data-aos]');
		animatedElements.forEach(el => {
			el.style.opacity = '1';
			el.style.transform = 'none';
		});
	}

	// Listen for changes (in case user changes preference)
	prefersReducedMotion.addEventListener('change', function (e) {
		if (e.matches) {
			// Reload page to ensure clean state, or manually reset all animations
			ScrollTrigger.getAll().forEach(st => st.kill());

			document.querySelectorAll('[style*="opacity: 0"]').forEach(el => {
				el.style.opacity = '1';
				el.style.transform = 'none';
			});
		}
	});
}

/**
 * Initialize all block animations
 */
document.addEventListener('DOMContentLoaded', function () {
	// Initialize reduced motion support first
	initReducedMotionSupport();

	// Initialize animations for each block type
	initHeroAnimations();
	initServicesAnimations();
	initServiceCategoriesAnimations();
	initValuesAnimations();
	initFAQAnimations();
	initContactAnimations();
	initFooterAnimations();
	initGenericBlockAnimations();
	initAboutAnimations();
	initVisionMissionAnimations();
	initSearchAnimations();
});

/**
 * Hero Block Animations
 */
function initHeroAnimations() {
	const heroSection = document.querySelector('.hero-block');

	if (!heroSection) {
		return;
	}

	// Animate hero content on load
	const heroTimeline = gsap.timeline();

	heroTimeline
		.from('.hero-block .hero-title', {
			y: 50,
			opacity: 0,
			duration: 1,
			ease: 'power3.out',
		})
		.from('.hero-block .hero-subtitle', {
			y: 30,
			opacity: 0,
			duration: 0.8,
			ease: 'power3.out',
		}, '-=0.5')
		.from('.hero-block .btn', {
			y: 20,
			opacity: 0,
			duration: 0.6,
			ease: 'power3.out',
		}, '-=0.3');
}

/**
 * Services Block Animations
 */
function initServicesAnimations() {
	const servicesSection = document.querySelector('.services-block-wrapper, .services-block');

	if (!servicesSection) {
		return;
	}

	// Animate services header
	const servicesHeader = document.querySelector('.services-block-wrapper .services-header, .services-block .editorial-header');
	if (servicesHeader) {
		gsap.from(servicesHeader, {
			scrollTrigger: {
				trigger: servicesHeader,
				start: 'top 90%',
				toggleActions: 'play none none none',
			},
			y: 30,
			opacity: 0,
			duration: 0.8,
			ease: 'power3.out',
		});
	}

	// Animate Service cards with stagger
	const serviceCards = document.querySelectorAll('.services-block-wrapper .service-card');
	if (serviceCards.length > 0) {
		gsap.set(serviceCards, { opacity: 0, y: 30 });
		gsap.to(serviceCards, {
			scrollTrigger: {
				trigger: '.services-block-wrapper .services-cards-grid',
				start: 'top 85%',
				toggleActions: 'play none none none',
			},
			opacity: 1,
			y: 0,
			duration: 0.6,
			stagger: 0.1,
			ease: 'power3.out',
		});
	}

}

/**
 * Service Categories Block Animations
 */
function initServiceCategoriesAnimations() {
	const categoriesSection = document.querySelector('.service-categories-block');

	if (!categoriesSection) {
		return;
	}

	// Animate Service Categories Header
	const scTitle = document.querySelector('.service-categories-wrapper .editorial-header');
	if (scTitle) {
		gsap.from(scTitle, {
			scrollTrigger: {
				trigger: scTitle,
				start: 'top 90%',
				toggleActions: 'play none none none',
			},
			y: 30,
			opacity: 0,
			duration: 0.8,
			ease: 'power3.out',
		});
	}

	// Animate Service Categories Vertical Label
	const scLabel = document.querySelector('.service-categories-wrapper .sc-label-wrap');
	if (scLabel) {
		gsap.from(scLabel, {
			scrollTrigger: {
				trigger: scLabel,
				start: 'top 85%',
				toggleActions: 'play none none none',
			},
			x: -30,
			opacity: 0,
			duration: 1,
			ease: 'power3.out',
		});
	}

	// Animate sc items with stagger
	const editorialScItems = document.querySelectorAll('.service-categories-wrapper .sc-item-editorial');
	if (editorialScItems.length > 0) {
		gsap.from(editorialScItems, {
			scrollTrigger: {
				trigger: '.service-categories-wrapper .sc-list-editorial',
				start: 'top 80%',
				toggleActions: 'play none none none',
			},
			y: 40,
			opacity: 0,
			duration: 0.6,
			stagger: 0.15,
			ease: 'power3.out',
		});
	}
}

/**
 * Values Block Animations
 */
function initValuesAnimations() {
	const valuesSection = document.querySelector('.values-block-wrapper');

	if (!valuesSection) {
		return;
	}

	// Handle Editorial Layout
	if (valuesSection.classList.contains('editorial-layout')) {
		// Animate Vertical Label & Number
		const valuesLabel = document.querySelector('.values-label-wrap');
		if (valuesLabel) {
			gsap.from(valuesLabel, {
				scrollTrigger: {
					trigger: valuesLabel,
					start: 'top 85%',
					toggleActions: 'play none none none',
				},
				x: -30,
				opacity: 0,
				duration: 1,
				ease: 'power3.out',
			});
		}

		// Animate Header Title
		const headerTitle = document.querySelector('.values-header-title');
		if (headerTitle) {
			gsap.from(headerTitle, {
				scrollTrigger: {
					trigger: headerTitle,
					start: 'top 85%',
					toggleActions: 'play none none none',
				},
				y: 30,
				opacity: 0,
				duration: 0.8,
				ease: 'power3.out',
			});
		}

		// Animate Luxury List Items with stagger
		const valueItems = document.querySelectorAll('.value-item-editorial');
		if (valueItems.length > 0) {
			gsap.from(valueItems, {
				scrollTrigger: {
					trigger: '.values-list-editorial',
					start: 'top 80%',
					toggleActions: 'play none none none',
				},
				y: 40,
				opacity: 0,
				duration: 0.8,
				stagger: 0.15,
				ease: 'power3.out',
			});
		}

		return; // Exit as handled by editorial logic
	}

	// Legacy Card Layout Animations
	// Animate section header
	gsap.from('.values-block .section-header', {
		scrollTrigger: {
			trigger: '.values-block .section-header',
			start: 'top 80%',
			toggleActions: 'play none none none',
		},
		y: 40,
		opacity: 0,
		duration: 0.8,
		ease: 'power3.out',
	});

	// Animate value cards with stagger
	const valueCards = document.querySelectorAll('.values-block-wrapper:not(.editorial-layout) .value-card');

	if (valueCards.length > 0) {
		gsap.from(valueCards, {
			scrollTrigger: {
				trigger: '.values-block-wrapper .values-grid',
				start: 'top 75%',
				toggleActions: 'play none none none',
			},
			y: 50,
			opacity: 0,
			duration: 0.7,
			stagger: 0.1,
			ease: 'power3.out',
		});
	}
}

/**
 * FAQ Block Animations
 */
function initFAQAnimations() {
	const faqSection = document.querySelector('.faq-block');

	if (!faqSection) {
		return;
	}

	// Handle Editorial Layout
	if (faqSection.classList.contains('editorial-layout')) {
		// Animate Vertical Label & Number
		const faqLabel = document.querySelector('.faq-label-wrap');
		if (faqLabel) {
			gsap.from(faqLabel, {
				scrollTrigger: {
					trigger: faqLabel,
					start: 'top 85%',
					toggleActions: 'play none none none',
				},
				x: -30,
				opacity: 0,
				duration: 1,
				ease: 'power3.out',
			});
		}

		// Animate Header Title
		const faqTitle = document.querySelector('.faq-block .editorial-header');
		if (faqTitle) {
			gsap.from(faqTitle, {
				scrollTrigger: {
					trigger: faqTitle,
					start: 'top 85%',
					toggleActions: 'play none none none',
				},
				y: 30,
				opacity: 0,
				duration: 0.8,
				ease: 'power3.out',
			});
		}

		// Animate FAQ items with stagger
		const faqItems = document.querySelectorAll('.faq-item-editorial');
		if (faqItems.length > 0) {
			gsap.from(faqItems, {
				scrollTrigger: {
					trigger: '.faq-accordion-editorial',
					start: 'top 80%',
					toggleActions: 'play none none none',
				},
				y: 40,
				opacity: 0,
				duration: 0.8,
				stagger: 0.15,
				ease: 'power3.out',
			});
		}
		return;
	}

	// Legacy Card Layout Animations
	// Animate section header
	gsap.from('.faq-block .section-header', {
		scrollTrigger: {
			trigger: '.faq-block .section-header',
			start: 'top 80%',
			toggleActions: 'play none none none',
		},
		y: 40,
		opacity: 0,
		duration: 0.8,
		ease: 'power3.out',
	});

	// Animate FAQ items with stagger
	const faqItemsLegacy = document.querySelectorAll('.faq-block .faq-item');
	if (faqItemsLegacy.length > 0) {
		gsap.from(faqItemsLegacy, {
			scrollTrigger: {
				trigger: '.faq-block .faq-accordion',
				start: 'top 75%',
				toggleActions: 'play none none none',
			},
			y: 30,
			opacity: 0,
			duration: 0.6,
			stagger: 0.1,
			ease: 'power3.out',
		});
	}
}

/**
 * Contact Block Animations
 */
function initContactAnimations() {
	const contactSection = document.querySelector('.contact-block');

	if (!contactSection) {
		return;
	}

	// ─── Editorial Layout Animations ──────────────────────────────────────────
	if (contactSection.classList.contains('editorial-layout')) {
		// Animate Side Label & Number
		const sideLabel = document.querySelector('.contact-label-wrap');
		if (sideLabel) {
			gsap.from(sideLabel, {
				scrollTrigger: {
					trigger: sideLabel,
					start: 'top 85%',
					toggleActions: 'play none none none',
				},
				y: 40,
				opacity: 0,
				duration: 1,
				ease: 'power4.out',
			});
		}

		// Animate form column
		const formColumn = document.querySelector('.contact-form-column');
		if (formColumn) {
			gsap.from(formColumn, {
				scrollTrigger: {
					trigger: formColumn,
					start: 'top 80%',
					toggleActions: 'play none none none',
				},
				y: 30,
				opacity: 0,
				duration: 0.8,
				ease: 'power3.out',
			});
		}

		// Animate info items with stagger
		const infoItems = document.querySelectorAll('.contact-info-item-editorial');
		if (infoItems.length > 0) {
			gsap.fromTo(infoItems,
				{ y: 20, opacity: 0 },
				{
					y: 0,
					opacity: 1,
					duration: 0.6,
					stagger: 0.1,
					ease: 'power2.out',
					scrollTrigger: {
						trigger: '.contact-info-list-editorial',
						start: 'top 95%',
						toggleActions: 'play none none none',
					},
					onComplete: () => {
						gsap.set(infoItems, { clearProps: 'opacity,transform,translate,rotate,scale' });
					}
				}
			);
		}

		// Animate social icons with stagger
		const socialIcons = document.querySelectorAll('.social-link-editorial');
		if (socialIcons.length > 0) {
			// Removed GSAP animation to prevent icons from getting stuck at opacity: 0
			// when the ScrollTrigger trigger point isn't reached or calculable on shorter pages.
			gsap.set(socialIcons, { clearProps: "all" });
		}
		return;
	}

	// ─── Legacy Card Layout Animations ────────────────────────────────────────
	// Animate contact form column
	const formColumnLegacy = document.querySelector('.contact-block .contact-form-column');
	if (formColumnLegacy) {
		gsap.from(formColumnLegacy, {
			scrollTrigger: {
				trigger: '.contact-block .contact-grid',
				start: 'top 75%',
				toggleActions: 'play none none none',
			},
			x: -50,
			opacity: 0,
			duration: 0.8,
			ease: 'power3.out',
			immediateRender: false,
		});
	}

	// Animate contact info column
	const infoColumnLegacy = document.querySelector('.contact-block .contact-info-column');
	if (infoColumnLegacy) {
		gsap.from(infoColumnLegacy, {
			scrollTrigger: {
				trigger: '.contact-block .contact-grid',
				start: 'top 75%',
				toggleActions: 'play none none none',
			},
			x: 50,
			opacity: 0,
			duration: 0.8,
			ease: 'power3.out',
			immediateRender: false,
		});
	}

	// Animate info items with stagger
	const infoItemsLegacy = document.querySelectorAll('.contact-block .info-item');
	if (infoItemsLegacy.length > 0) {
		gsap.from(infoItemsLegacy, {
			scrollTrigger: {
				trigger: '.contact-block .contact-info-card',
				start: 'top 70%',
				toggleActions: 'play none none none',
			},
			y: 20,
			opacity: 0,
			duration: 0.5,
			stagger: 0.1,
			ease: 'power3.out',
			immediateRender: false,
		});
	}

	// Animate social links
	const socialLinksLegacy = document.querySelectorAll('.contact-block .social-link');
	if (socialLinksLegacy.length > 0) {
		gsap.from(socialLinksLegacy, {
			scrollTrigger: {
				trigger: '.contact-block .social-links',
				start: 'top 85%',
				toggleActions: 'play none none none',
			},
			scale: 0.8,
			opacity: 0,
			duration: 0.4,
			stagger: 0.08,
			ease: 'back.out(1.7)',
			immediateRender: false,
		});
	}
}

/**
 * Footer Animations
 */
function initFooterAnimations() {
	const footer = document.querySelector('.footer');

	if (!footer) {
		return;
	}

	// Animate footer content
	const footerTimeline = gsap.timeline({
		scrollTrigger: {
			trigger: '.footer',
			start: 'top 90%',
			toggleActions: 'play none none none',
		},
	});

	footerTimeline
		.from('.footer .footer-tagline', {
			y: 30,
			opacity: 0,
			duration: 0.8,
			ease: 'power3.out',
		})
		.from('.footer .footer-nav-list li', {
			y: 20,
			opacity: 0,
			duration: 0.5,
			stagger: 0.08,
			ease: 'power3.out',
		}, '-=0.4')
		.from('.footer .copyright', {
			y: 15,
			opacity: 0,
			duration: 0.5,
			ease: 'power3.out',
		}, '-=0.2');
}

/**
 * Generic Block Animations
 * For any other sections with standard patterns
 */
function initGenericBlockAnimations() {
	// Animate CTA sections
	const ctaSections = document.querySelectorAll('.cta-section');

	ctaSections.forEach(function (section) {
		gsap.from(section.querySelector('.cta-content'), {
			scrollTrigger: {
				trigger: section,
				start: 'top 80%',
				toggleActions: 'play none none none',
			},
			y: 40,
			opacity: 0,
			duration: 0.8,
			ease: 'power3.out',
		});
	});

	// Animate process/timeline sections
	const processSteps = document.querySelectorAll('.process-step');

	if (processSteps.length > 0) {
		gsap.from(processSteps, {
			scrollTrigger: {
				trigger: '.process-timeline',
				start: 'top 75%',
				toggleActions: 'play none none none',
			},
			x: -30,
			opacity: 0,
			duration: 0.7,
			stagger: 0.15,
			ease: 'power3.out',
		});
	}

	// Animate project cards (work/portfolio sections)
	const projectCards = document.querySelectorAll('.project-card');

	if (projectCards.length > 0) {
		// Animate immediately on page load without waiting for scroll
		// This ensures cards are visible even when content is already in view
		gsap.fromTo(projectCards, 
			{ opacity: 0, y: 30 },
			{ 
				opacity: 1, 
				y: 0,
				duration: 0.7, 
				stagger: 0.12, 
				ease: 'power3.out',
				// Run immediately on page load
				delay: 0.1,
				// Ensure it completes even if page is already scrolled
				lazy: false,
			}
		);
	}
}

/**
 * About Block Animations
 */
function initAboutAnimations() {
	const aboutSection = document.querySelector('.about-block-wrapper');

	if (!aboutSection) {
		return;
	}

	// Check if this is the two-column layout
	const isTwoColumn = aboutSection.classList.contains('layout-two-column');

	if (isTwoColumn) {
		// ==========================================
		// TWO COLUMN LAYOUT ANIMATIONS
		// ==========================================

		// Animate left column items (numbered points) with stagger
		const leftColItems = document.querySelectorAll('.about-block-wrapper.layout-two-column .left-col-item');
		if (leftColItems.length > 0) {
			// Set initial state
			gsap.set(leftColItems, { opacity: 0, y: 30 });
			
			gsap.to(leftColItems, {
				scrollTrigger: {
					trigger: '.about-block-wrapper.layout-two-column .about-left-col',
					start: 'top 80%',
					toggleActions: 'play none none none',
				},
				opacity: 1,
				y: 0,
				duration: 0.6,
				stagger: 0.15,
				ease: 'power3.out',
			});
		}

		// Animate team section in left column
		const teamSection = document.querySelector('.about-block-wrapper.layout-two-column .about-team-section');
		const teamCards = document.querySelectorAll('.about-block-wrapper.layout-two-column .about-team-card');
		if (teamSection && teamCards.length > 0) {
			gsap.set(teamCards, { opacity: 0, y: 20 });
			gsap.to(teamCards, {
				scrollTrigger: {
					trigger: teamSection,
					start: 'top 85%',
					toggleActions: 'play none none none',
				},
				opacity: 1,
				y: 0,
				duration: 0.5,
				stagger: 0.08,
				ease: 'power3.out',
			});
		}


		// Animate right column - story section
		const storySection = document.querySelector('.about-block-wrapper.layout-two-column .about-story-section');
		if (storySection) {
			gsap.from(storySection, {
				scrollTrigger: {
					trigger: storySection,
					start: 'top 85%',
					toggleActions: 'play none none none',
				},
				y: 40,
				opacity: 0,
				duration: 0.8,
				ease: 'power3.out',
			});
		}

		// Animate right column - video section
		const videoSection = document.querySelector('.about-block-wrapper.layout-two-column .about-video-section');
		if (videoSection) {
			gsap.from(videoSection, {
				scrollTrigger: {
					trigger: videoSection,
					start: 'top 85%',
					toggleActions: 'play none none none',
				},
				y: 40,
				opacity: 0,
				duration: 0.8,
				delay: 0.2,
				ease: 'power3.out',
			});
		}

		// Animate right column - social section
		const socialSection = document.querySelector('.about-block-wrapper.layout-two-column .about-social-section');
		if (socialSection) {
			gsap.from(socialSection, {
				scrollTrigger: {
					trigger: socialSection,
					start: 'top 85%',
					toggleActions: 'play none none none',
				},
				y: 40,
				opacity: 0,
				duration: 0.8,
				delay: 0.4,
				ease: 'power3.out',
			});
		}

		// Animate social links with stagger
		const socialLinks = document.querySelectorAll('.about-block-wrapper.layout-two-column .about-social-link');
		if (socialLinks.length > 0) {
			gsap.set(socialLinks, { opacity: 0, y: 15 });
			
			gsap.to(socialLinks, {
				scrollTrigger: {
					trigger: '.about-block-wrapper.layout-two-column .about-social-links',
					start: 'top 90%',
					toggleActions: 'play none none none',
				},
				opacity: 1,
				y: 0,
				duration: 0.4,
				stagger: 0.1,
				ease: 'power2.out',
			});
		}

		return; // Skip original animations for two-column layout
	}

	// Original single-column layout animations remain the same
	// Animate main content card
	const aboutContent = document.querySelector('.about-block-wrapper .about-content');
	if (aboutContent) {
		gsap.fromTo(aboutContent,
			{ y: 50, opacity: 0 },
			{
				scrollTrigger: {
					trigger: aboutContent,
					start: 'top 85%',
					toggleActions: 'play none none none',
				},
				y: 0,
				opacity: 1,
				duration: 0.9,
				ease: 'power3.out',
			}
		);
	}

	// Animate brand essence section title (Editorial)
	const essenceTitle = document.querySelector('.about-block-wrapper .brand-essence-header-title');
	if (essenceTitle) {
		gsap.from(essenceTitle, {
			scrollTrigger: {
				trigger: essenceTitle,
				start: 'top 90%',
				toggleActions: 'play none none none',
			},
			y: 30,
			opacity: 0,
			duration: 0.8,
			ease: 'power3.out',
		});
	}

	// Animate Brand Essence Vertical Label
	const essenceLabel = document.querySelector('.about-block-wrapper .essence-label-wrap');
	if (essenceLabel) {
		gsap.from(essenceLabel, {
			scrollTrigger: {
				trigger: essenceLabel,
				start: 'top 85%',
				toggleActions: 'play none none none',
			},
			x: -30,
			opacity: 0,
			duration: 1,
			ease: 'power3.out',
		});
	}

	// Animate Brand Essence items with stagger
	const editorialEssenceItems = document.querySelectorAll('.about-block-wrapper .essence-item-editorial');
	if (editorialEssenceItems.length > 0) {
		gsap.from(editorialEssenceItems, {
			scrollTrigger: {
				trigger: '.about-block-wrapper .brand-essence-list-editorial',
				start: 'top 80%',
				toggleActions: 'play none none none',
			},
			y: 40,
			opacity: 0,
			duration: 0.6,
			stagger: 0.15,
			ease: 'power3.out',
		});
	}
}

/**
 * Vision & Mission Block Animations
 */
function initVisionMissionAnimations() {
	const vmSection = document.querySelector('.vision-mission-block-wrapper');

	if (!vmSection) {
		return;
	}

	// Animate Vision Label & Number
	const visionLabel = document.querySelector('.vision-label-wrap');
	if (visionLabel) {
		gsap.from(visionLabel, {
			scrollTrigger: {
				trigger: visionLabel,
				start: 'top 85%',
				toggleActions: 'play none none none',
			},
			x: -30,
			opacity: 0,
			duration: 1,
			ease: 'power3.out',
		});
	}

	// Animate Vision Title
	const visionTitle = document.querySelector('.vision-header-title');
	if (visionTitle) {
		gsap.from(visionTitle, {
			scrollTrigger: {
				trigger: visionTitle,
				start: 'top 85%',
				toggleActions: 'play none none none',
			},
			y: 30,
			opacity: 0,
			duration: 0.8,
			ease: 'power3.out',
		});
	}

	// Animate Vision Text
	const visionText = document.querySelector('.vision-body-text');
	if (visionText) {
		gsap.from(visionText, {
			scrollTrigger: {
				trigger: visionText,
				start: 'top 85%',
				toggleActions: 'play none none none',
			},
			y: 20,
			opacity: 0,
			duration: 1,
			delay: 0.2,
			ease: 'power3.out',
		});
	}

	// Animate Mission Title
	const missionTitle = document.querySelector('.mission-header-title');
	if (missionTitle) {
		gsap.from(missionTitle, {
			scrollTrigger: {
				trigger: missionTitle,
				start: 'top 90%',
				toggleActions: 'play none none none',
			},
			y: 30,
			opacity: 0,
			duration: 0.8,
			ease: 'power3.out',
		});
	}

	// Animate Mission Items with stagger
	const missionItems = document.querySelectorAll('.mission-item-editorial');
	if (missionItems.length > 0) {
		gsap.from(missionItems, {
			scrollTrigger: {
				trigger: '.mission-list-editorial',
				start: 'top 80%',
				toggleActions: 'play none none none',
			},
			y: 40,
			opacity: 0,
			duration: 0.8,
			stagger: 0.15,
			ease: 'power3.out',
		});
	}
}

/**
 * Search Results Animations
 */
function initSearchAnimations() {
	const searchWrapper = document.querySelector('.search-page-container');
	if (!searchWrapper) return;

	// Animate Side Label
	const sideLabel = searchWrapper.querySelector('.search-label-wrap');
	if (sideLabel) {
		gsap.from(sideLabel, {
			scrollTrigger: {
				trigger: sideLabel,
				start: 'top 90%',
				toggleActions: 'play none none none',
			},
			x: -30,
			opacity: 0,
			duration: 1,
			ease: 'power3.out',
		});
	}

	// Animate Header Content
	const headerContent = searchWrapper.querySelector('.search-header-editorial');
	if (headerContent) {
		gsap.from(headerContent.children, {
			scrollTrigger: {
				trigger: headerContent,
				start: 'top 85%',
				toggleActions: 'play none none none',
			},
			y: 30,
			opacity: 0,
			duration: 0.8,
			stagger: 0.2,
			ease: 'power3.out',
		});
	}

	// Animate Result Items individually
	const resultItems = searchWrapper.querySelectorAll('.search-result-item-editorial');
	resultItems.forEach((item, index) => {
		gsap.from(item, {
			scrollTrigger: {
				trigger: item,
				start: 'top 92%',
				toggleActions: 'play none none none',
			},
			y: 30,
			opacity: 0,
			duration: 0.8,
			delay: index < 5 ? index * 0.1 : 0, // Stagger only the first few for initial load feel
			ease: 'power3.out',
		});
	});

	// Animate Sidebar Widgets
	const sidebarWidgets = searchWrapper.querySelectorAll('.sidebar-widget-editorial');
	if (sidebarWidgets.length > 0) {
		gsap.from(sidebarWidgets, {
			scrollTrigger: {
				trigger: '.search-sidebar-editorial',
				start: 'top 85%',
				toggleActions: 'play none none none',
			},
			x: 30,
			opacity: 0,
			duration: 0.8,
			stagger: 0.2,
			ease: 'power3.out',
		});
	}
}
